<template>
  <!--
    SaleTechnicalDrawingPanel
    -------------------------------------------------------------------------
    Editor de desenho técnico embutido na coluna do meio da tela de venda.
    SÓ aparece quando uma peça é clicada na lista (in_preparation), via Vuex
    (get_drawing_selection). Mostra claramente qual produto/peça está em edição
    e persiste as alterações com `atualizar_gestordepedido` (debounced).
  -->
  <v-flex
    v-if="isOpen"
    ref="root"
    xs12
    class="sale-drawing-panel"
    :class="{ 'sale-drawing-panel-dark': isDark }"
  >
    <div class="sdp-card" :class="{ 'sdp-card--expanded': expanded }">
      <div v-show="!expanded" class="sdp-head">
        <div class="sdp-head-title">
          <v-icon size="18" :color="$theme.primary">mdi-ruler-square-compass</v-icon>
          <div class="column">
            <span class="sdp-title">
              Desenho técnico · {{ selectedProduct.descricao_personalizada || selectedProduct.nome }}
            </span>
            <span class="sdp-sub">
              Editando: <strong>{{ selectedPieceLabel }}</strong>
            </span>
          </div>
        </div>
        <div class="sdp-head-actions">
          <span v-if="saveState !== 'idle'" class="sdp-save" :class="`sdp-save--${saveState}`">
            <v-icon size="13" class="sdp-save-icon">{{ saveStateIcon }}</v-icon>
            {{ saveStateLabel }}
          </span>
          <v-btn
            small
            depressed
            :color="$theme.primary"
            class="white--text sdp-print-btn"
            @click="$emit('print-technical-drawing')"
          >
            <v-icon left size="16">mdi-printer-outline</v-icon>
            Imprimir desenho
          </v-btn>
          <v-btn
            icon
            small
            title="Salvar esta peça como Forma Pronta (template reutilizável)"
            @click="openSaveAsReadyForm"
          >
            <v-icon size="20">mdi-content-save-move-outline</v-icon>
          </v-btn>
          <v-btn
            icon
            small
            :title="expanded ? 'Restaurar tamanho' : 'Expandir editor'"
            @click="expanded = !expanded"
          >
            <v-icon size="20">{{ expanded ? "mdi-arrow-collapse" : "mdi-arrow-expand" }}</v-icon>
          </v-btn>
          <v-btn icon small @click="close" title="Fechar editor">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- alterna entre produtos da venda sem voltar para a lista -->
      <div v-if="produtos.length > 1 && !expanded" class="sdp-products">
        <span class="sdp-products-label">Produto:</span>
        <v-chip
          v-for="(prod, pi) in produtos"
          :key="`sdp-prod-${pi}`"
          small
          label
          :color="pi === activeProductIndex ? $theme.primary : ''"
          :text-color="pi === activeProductIndex ? 'white' : ''"
          class="sdp-product-chip"
          @click="selectProduct(pi)"
        >
          {{ prod.descricao_personalizada || prod.nome }}
        </v-chip>
      </div>

      <div class="sdp-editor">
        <StoneTechnicalDrawing2D
          :key="`drawing-${activeProductIndex}-${expanded}`"
          :items="selectedProduct.ficha_tecnica || []"
          :selected-index="selectedPieceIndex"
          :tax-finishing="30"
          :plate-image="selectedProduct.imagens && selectedProduct.imagens[0]"
          :slab-thickness-mm="selectedSlabThicknessMm"
          :drawing-elements="drawingExtras.elements"
          :drawing-annotations="drawingExtras.annotations"
          :drawing-images="drawingExtras.images"
          :item-recalculator="recalculateFichaItem"
          :finish-catalog="finishCatalog"
          :secondary-catalog="secondaryCatalog"
          :cutout-catalog="cutoutCatalog"
          :height="expanded ? 760 : 480"
          :board-title="`Desenho técnico`"
          :board-subtitle="expanded ? (selectedProduct.descricao_personalizada || selectedProduct.nome || '') : ''"
          @select-item="onSelectPiece"
          @update:items="onUpdateItems"
          @piece-resized="onPieceResized"
          @notify="msg => createGlobalMessage({ type: msg.type || 'info', message: msg.message, timeout: 4000 })"
          @add-element="onDrawingAdd('elements', $event)"
          @update-element="onDrawingUpdate('elements', $event)"
          @remove-element="onDrawingRemove('elements', $event)"
          @add-annotation="onDrawingAdd('annotations', $event)"
          @update-annotation="onDrawingUpdate('annotations', $event)"
          @remove-annotation="onDrawingRemove('annotations', $event)"
          @add-image="onDrawingAdd('images', $event)"
          @update-image="onDrawingUpdate('images', $event)"
          @remove-image="onDrawingRemove('images', $event)"
          @replace-extras="onReplaceExtras"
        >
          <template v-slot:board-actions>
            <span v-if="expanded && saveState !== 'idle'" class="sdp-save" :class="`sdp-save--${saveState}`">
              <v-icon size="13" class="sdp-save-icon">{{ saveStateIcon }}</v-icon>
              {{ saveStateLabel }}
            </span>
            <v-btn
              v-if="expanded"
              small
              depressed
              :color="$theme.primary"
              class="white--text sdp-print-btn"
              @click="$emit('print-technical-drawing')"
            >
              <v-icon left size="16">mdi-printer-outline</v-icon>
              Imprimir
            </v-btn>
            <v-btn
              v-if="expanded"
              icon
              small
              title="Restaurar tamanho"
              @click="expanded = false"
            >
              <v-icon size="20">mdi-arrow-collapse</v-icon>
            </v-btn>
          </template>
        </StoneTechnicalDrawing2D>
      </div>

      <!-- salvar a peça atual como Forma Pronta (template reutilizável) -->
      <v-dialog v-model="readyFormDialog" max-width="420" persistent>
        <v-card class="pa-4" style="border-radius: 14px;">
          <span class="sdp-title">Salvar como Forma Pronta</span>
          <p class="sdp-sub mt-1 mb-2">
            O desenho desta peça (medidas, silhueta, recortes e acabamentos)
            vira um template reutilizável no orçamento. Os preços são
            recalculados na hora de usar, conforme a placa escolhida.
          </p>
          <v-text-field
            v-model="readyFormName"
            dense
            outlined
            autofocus
            label="Nome da Forma Pronta"
            placeholder="ex.: Bancada em L com cuba"
            hide-details
            @keyup.enter="saveAsReadyForm"
          />
          <div class="expande-horizontal mt-3" style="display: flex; gap: 8px; justify-content: flex-end;">
            <v-btn text small @click="readyFormDialog = false">Cancelar</v-btn>
            <v-btn
              small
              depressed
              :color="$theme.primary"
              class="white--text"
              :loading="savingReadyForm"
              @click="saveAsReadyForm"
            >
              Salvar template
            </v-btn>
          </div>
        </v-card>
      </v-dialog>

      <!-- aviso de alteração de medida/preço com Desfazer -->
      <v-snackbar
        v-model="resizeToast.show"
        :timeout="6000"
        bottom
        right
      >
        {{ resizeToast.message }}
        <template #action="{ attrs }">
          <v-btn text color="white" v-bind="attrs" @click="undoResize">
            Desfazer
          </v-btn>
        </template>
      </v-snackbar>
    </div>
  </v-flex>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import StoneTechnicalDrawing2D from "@/components/stone-technical-drawing/StoneTechnicalDrawing2D.vue";
import { recalculateItem, deriveTaxFinishing } from "@/apps/shared/services/fichaPricing";
import { formatMeasure } from "@/apps/shared/utils/measurements";

export default {
  name: "SaleTechnicalDrawingPanel",
  components: { StoneTechnicalDrawing2D },
  data() {
    return {
      saveTimer: null,
      saveResetTimer: null,
      // idle | saving | saved | error — feedback de persistência no header
      saveState: "idle",
      // editor em tela cheia (overlay fixo)
      expanded: false,
      // dialog "Salvar como Forma Pronta"
      readyFormDialog: false,
      readyFormName: "",
      savingReadyForm: false,
      // insumos cadastrados (catálogo de acabamentos/peças p/ o desenho)
      insumosCatalog: [],
      // aviso de medida/preço alterados pelo desenho, com Desfazer
      resizeToast: {
        show: false,
        message: "",
        productIndex: null,
        sourceIndex: null,
        before: null
      }
    };
  },
  computed: {
    ...mapGetters(["get_gestordepedido", "get_drawing_selection"]),
    isDark() {
      return localStorage.dark === "true";
    },
    produtos() {
      const compra = this.get_gestordepedido && this.get_gestordepedido.compra;
      return (compra && compra.produtos) || [];
    },
    selection() {
      return this.get_drawing_selection || { productIndex: null, pieceIndex: null };
    },
    // o painel só abre quando há um produto explicitamente selecionado (clicado)
    isOpen() {
      const idx = this.selection.productIndex;
      return idx !== null && idx !== undefined && !!this.produtos[idx];
    },
    activeProductIndex() {
      return this.isOpen ? this.selection.productIndex : -1;
    },
    selectedProduct() {
      return this.produtos[this.activeProductIndex] || null;
    },
    selectedSlabThicknessMm() {
      const product = this.selectedProduct || {};
      const raw =
        product.slabThicknessMm ||
        product.espessura_mm ||
        product.espessuraMm ||
        product.grossura_mm ||
        product.grossuraMm;
      const value = parseFloat(`${raw || ""}`.replace(",", "."));
      return value > 0 ? value : null;
    },
    selectedPieceIndex() {
      const pi = this.selection.pieceIndex;
      return pi === null || pi === undefined ? null : pi;
    },
    selectedPieceLabel() {
      const ficha =
        (this.selectedProduct && this.selectedProduct.ficha_tecnica) || [];
      const item = ficha[this.selectedPieceIndex];
      if (!item) return "peça";
      const prefix =
        item.type === "Acabamento"
          ? "AC"
          : item.type === "Peça Principal"
            ? "P"
            : "S";
      const desc = item.descricao || item.type || "peça";
      return `${prefix}${(this.selectedPieceIndex || 0) + 1} - ${desc}`;
    },
    drawingExtras() {
      const extras = (this.selectedProduct && this.selectedProduct.drawingExtras) || {};
      return {
        elements: Array.isArray(extras.elements) ? extras.elements : [],
        annotations: Array.isArray(extras.annotations) ? extras.annotations : [],
        images: Array.isArray(extras.images) ? extras.images : []
      };
    },
    saveStateLabel() {
      if (this.saveState === "saving") return "Salvando…";
      if (this.saveState === "saved") return "Salvo";
      if (this.saveState === "error") return "Erro ao salvar";
      return "";
    },
    saveStateIcon() {
      if (this.saveState === "saving") return "mdi-cloud-upload-outline";
      if (this.saveState === "saved") return "mdi-cloud-check-outline";
      return "mdi-cloud-alert";
    },
    normalizeInsumoType() {
      return type =>
        `${type || ""}`
          .normalize("NFD")
          .replace(/[̀-ͯ]/g, "")
          .toLowerCase();
    },
    // acabamentos cadastrados (com imagem) — viram paleta arrastável
    finishCatalog() {
      return this.insumosCatalog.filter(
        i =>
          this.normalizeInsumoType(i.type).includes("acabamento") &&
          i.disponivel !== false
      );
    },
    // peças secundárias cadastradas (saia/rodapé) — sugestão-fantasma
    secondaryCatalog() {
      return this.insumosCatalog.filter(
        i =>
          this.normalizeInsumoType(i.type).includes("peca secundaria") &&
          i.disponivel !== false
      );
    },
    // furos cadastrados (medidas em cm + símbolo) — dropdown de recortes
    cutoutCatalog() {
      return this.insumosCatalog.filter(
        i =>
          this.normalizeInsumoType(i.type) === "furo" &&
          i.disponivel !== false
      );
    }
  },
  watch: {
    // ao abrir/trocar de peça, rola o editor para a área visível
    isOpen(open) {
      if (open) this.scrollIntoView();
    },
    "selection.pieceIndex"() {
      if (this.isOpen) this.scrollIntoView();
    }
  },
  methods: {
    ...mapActions(["atualizar_gestordepedido", "createGlobalMessage"]),
    scrollIntoView() {
      this.$nextTick(() => {
        const el = this.$refs.root && this.$refs.root.$el ? this.$refs.root.$el : this.$refs.root;
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      });
    },
    close() {
      // não deixa edição pendente para trás ao fechar o editor
      this.flushPersist();
      this.$store.commit("set_drawing_selection", {
        productIndex: null,
        pieceIndex: null
      });
    },
    selectProduct(productIndex) {
      this.$store.commit("set_drawing_selection", { productIndex, pieceIndex: 0 });
    },
    onSelectPiece(pieceIndex) {
      this.$store.commit("set_drawing_selection", {
        productIndex: this.activeProductIndex,
        pieceIndex
      });
    },
    // toda mutação de produto passa pela mutation dedicada (nada de $set
    // direto no objeto do getter)
    patchProduct(patch) {
      this.$store.commit("patch_produto_venda", {
        productIndex: this.activeProductIndex,
        patch
      });
      this.persist();
    },
    onUpdateItems(nextFicha) {
      if (!this.selectedProduct) return;
      // rollup financeiro do produto (mesma regra de processFinalPrice do
      // modal: seller_price = soma dos totais da ficha) + totais da venda
      const ficha_total_price = (nextFicha || []).reduce(
        (sum, item) =>
          item && item.quantity ? sum + (parseFloat(item.total) || 0) : sum,
        0
      );
      this.patchProduct({
        ficha_tecnica: nextFicha,
        seller_price: ficha_total_price,
        ficha_total_price
      });
      this.$store.commit("recalcular_totais_venda");
    },
    // recálculo injetado no engine: usa o preço da placa salvo no próprio
    // item e a taxa % derivada do item (taxa/calculo) — não depende da
    // config atual do caixa. Itens NOVOS (saia/acabamento criados pelo
    // desenho) herdam placa e taxa dos itens já existentes na ficha.
    recalculateFichaItem(item) {
      const ficha = (this.selectedProduct && this.selectedProduct.ficha_tecnica) || [];
      const reference = ficha.find(f => parseFloat(f.calculo) > 0);
      const thicknessReference = ficha.find(
        f => f && (f.slabThicknessMm || f.espessura_mm || f.espessuraMm)
      );
      const slabThicknessMm = this.itemThicknessMm(item, thicknessReference);
      const itemWithThickness = {
        ...item,
        slabThicknessMm,
        espessura_mm: slabThicknessMm,
        espessuraMm: slabThicknessMm
      };
      const platePrice =
        parseFloat(itemWithThickness.preco) ||
        (reference && parseFloat(reference.preco)) ||
        parseFloat(
          `${(this.selectedProduct && this.selectedProduct.ultimo_preco_de_compra) || 0}`.replace(",", ".")
        ) ||
        0;
      const taxFinishing =
        parseFloat(itemWithThickness.calculo) > 0
          ? deriveTaxFinishing(itemWithThickness)
          : reference
            ? deriveTaxFinishing(reference)
            : 30;
      return recalculateItem(itemWithThickness, {
        platePrice,
        taxFinishing,
        // acabamento novo sem preço salvo: usa o preço do próprio insumo
        resolveFinishingPrice: fichaItem => fichaItem.ultimo_preco_de_compra || 0
      });
    },
    itemThicknessMm(item, reference) {
      const raw =
        (item && (item.slabThicknessMm || item.espessura_mm || item.espessuraMm)) ||
        (reference &&
          (reference.slabThicknessMm || reference.espessura_mm || reference.espessuraMm)) ||
        (this.selectedProduct &&
          (this.selectedProduct.slabThicknessMm ||
            this.selectedProduct.espessura_mm ||
            this.selectedProduct.espessuraMm ||
            this.selectedProduct.grossura_mm ||
            this.selectedProduct.grossuraMm));
      const value = parseFloat(`${raw || ""}`.replace(",", "."));
      return value > 0 ? value : 10;
    },
    async loadInsumosCatalog() {
      try {
        const response = await this.$run("insumos/list", { all: true });
        const data = response.data || {};
        this.insumosCatalog = Array.isArray(data.docs)
          ? data.docs
          : Array.isArray(data)
            ? data
            : [];
      } catch (error) {
        this.insumosCatalog = [];
      }
    },
    onPieceResized({ sourceIndex, before, after }) {
      const fmt = v => formatMeasure(v, ",");
      const money = v =>
        this.$helper && this.$helper.formataSaldo
          ? this.$helper.formataSaldo(parseFloat(v) || 0)
          : `R$ ${(parseFloat(v) || 0).toFixed(2)}`;
      const desc = before.descricao || before.type || "Peça";
      let message = `${desc}: ${fmt(before.comprimento)}×${fmt(before.largura)}m → ${fmt(after.comprimento)}×${fmt(after.largura)}m`;
      if (parseFloat(before.total) !== parseFloat(after.total)) {
        message += ` · ${money(before.total)} → ${money(after.total)}`;
      }
      this.resizeToast = {
        show: true,
        message,
        productIndex: this.activeProductIndex,
        sourceIndex,
        before
      };
    },
    undoResize() {
      const { productIndex, sourceIndex, before } = this.resizeToast;
      this.resizeToast.show = false;
      const produto = this.produtos[productIndex];
      if (!produto || !Array.isArray(produto.ficha_tecnica)) return;
      const ficha = [...produto.ficha_tecnica];
      if (!ficha[sourceIndex]) return;
      ficha[sourceIndex] = before;
      const ficha_total_price = ficha.reduce(
        (sum, item) =>
          item && item.quantity ? sum + (parseFloat(item.total) || 0) : sum,
        0
      );
      this.$store.commit("patch_produto_venda", {
        productIndex,
        patch: {
          ficha_tecnica: ficha,
          seller_price: ficha_total_price,
          ficha_total_price
        }
      });
      this.$store.commit("recalcular_totais_venda");
      this.persist();
    },
    // cópia mutável dos extras atuais (com defaults + versão do schema)
    cloneExtras() {
      const extras =
        (this.selectedProduct && this.selectedProduct.drawingExtras) || {};
      return {
        ...extras,
        schemaVersion: 2,
        elements: Array.isArray(extras.elements) ? [...extras.elements] : [],
        annotations: Array.isArray(extras.annotations) ? [...extras.annotations] : [],
        images: Array.isArray(extras.images) ? [...extras.images] : []
      };
    },
    onDrawingAdd(layer, entity) {
      if (!this.selectedProduct) return;
      const extras = this.cloneExtras();
      extras[layer].push(entity);
      this.patchProduct({ drawingExtras: extras });
    },
    onDrawingUpdate(layer, { id, patch }) {
      if (!this.selectedProduct) return;
      const extras = this.cloneExtras();
      extras[layer] = extras[layer].map(e =>
        e.id === id ? { ...e, ...patch } : e
      );
      this.patchProduct({ drawingExtras: extras });
    },
    onDrawingRemove(layer, { id }) {
      if (!this.selectedProduct) return;
      const extras = this.cloneExtras();
      extras[layer] = extras[layer].filter(e => e.id !== id);
      this.patchProduct({ drawingExtras: extras });
    },
    // undo/redo restauram as 3 camadas de uma vez
    onReplaceExtras({ elements, annotations, images }) {
      if (!this.selectedProduct) return;
      const extras = this.cloneExtras();
      extras.elements = Array.isArray(elements) ? elements : [];
      extras.annotations = Array.isArray(annotations) ? annotations : [];
      extras.images = Array.isArray(images) ? images : [];
      this.patchProduct({ drawingExtras: extras });
    },
    openSaveAsReadyForm() {
      if (!this.selectedProduct || !(this.selectedProduct.ficha_tecnica || []).length) {
        this.createGlobalMessage({
          type: "info",
          message: "A peça precisa ter itens na ficha técnica para virar Forma Pronta.",
          timeout: 4000
        });
        return;
      }
      this.readyFormName =
        this.selectedProduct.descricao_personalizada || this.selectedProduct.nome || "";
      this.readyFormDialog = true;
    },
    async saveAsReadyForm() {
      const nome = `${this.readyFormName || ""}`.trim();
      if (!nome || this.savingReadyForm) return;
      // template não carrega preço: applyReadyForm recalcula na aplicação
      // com a placa escolhida no orçamento
      const itensLimpos = (this.selectedProduct.ficha_tecnica || []).map(item => {
        const {
          preco, calculo, taxa, total, is_done, // eslint-disable-line no-unused-vars
          ...template
        } = item;
        return template;
      });
      this.savingReadyForm = true;
      try {
        await this.$run("insumos/create", {
          nome,
          type: "Forma Pronta",
          disponivel: true,
          unidade_estoque_minimo: { nome: "Metro Quadrado", valor: "M2", divisor: 100 },
          preco_multiplo: [],
          ficha_tecnica: itensLimpos
        });
        this.readyFormDialog = false;
        this.createGlobalMessage({
          type: "success",
          message: `Forma Pronta "${nome}" criada! Ela já aparece no orçamento.`,
          timeout: 5000
        });
      } catch (error) {
        this.createGlobalMessage({
          type: "error",
          message:
            (error.response && error.response.data && error.response.data.message) ||
            "Não foi possível salvar a Forma Pronta.",
          timeout: 6000
        });
      } finally {
        this.savingReadyForm = false;
      }
    },
    persist() {
      this.saveState = "saving";
      if (this.saveTimer) clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => this.runSave(), 900);
    },
    // dispara o save AGORA se houver edição pendente (fechar/destruir)
    flushPersist() {
      if (!this.saveTimer) return;
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      this.runSave();
    },
    runSave() {
      this.saveTimer = null;
      const result = this.atualizar_gestordepedido(true);
      Promise.resolve(result).then(ok => {
        // uma nova edição durante o save reinicia o ciclo — não sobrescreve
        if (this.saveTimer) return;
        this.saveState = ok === false ? "error" : "saved";
        if (this.saveResetTimer) clearTimeout(this.saveResetTimer);
        if (this.saveState === "saved") {
          this.saveResetTimer = setTimeout(() => (this.saveState = "idle"), 2500);
        }
      });
    }
  },
  created() {
    this.loadInsumosCatalog();
  },
  beforeDestroy() {
    // salva o que estiver pendente antes de sair da tela
    this.flushPersist();
    if (this.saveResetTimer) clearTimeout(this.saveResetTimer);
  }
};
</script>

<style scoped>
.sale-drawing-panel {
  margin-top: 6px;
}
.sdp-card {
  background: #ffffff;
  border: 1px solid #128c7e;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  margin: 0 12px 12px;
  box-shadow: 0 6px 20px rgba(18, 140, 126, 0.12);
}
.sdp-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.sdp-head-title {
  display: flex;
  align-items: center;
  gap: 8px;
}
.sdp-head-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}
.sdp-title {
  font-weight: 700;
  font-size: 14px;
  color: #173b33;
}
.sdp-sub {
  font-size: 11px;
  color: #74827c;
}
.sdp-sub strong {
  color: #128c7e;
}
.sdp-print-btn {
  border-radius: 10px !important;
}
.sdp-save {
  align-items: center;
  border-radius: 8px;
  display: inline-flex;
  font-size: 11px;
  font-weight: 600;
  gap: 4px;
  padding: 3px 8px;
}
.sdp-save--saving {
  background: rgba(45, 108, 223, 0.1);
  color: #2d6cdf;
}
.sdp-save--saved {
  background: rgba(18, 140, 126, 0.12);
  color: #128c7e;
}
.sdp-save--error {
  background: rgba(216, 67, 67, 0.12);
  color: #d84343;
}
.sdp-save--saving .sdp-save-icon,
.sdp-save--saving.sdp-save .v-icon {
  color: #2d6cdf !important;
}
.sdp-save--saved .v-icon {
  color: #128c7e !important;
}
.sdp-save--error .v-icon {
  color: #d84343 !important;
}
.sdp-products {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}
.sdp-products-label {
  font-size: 11px;
  color: #74827c;
}
.sdp-product-chip {
  border-radius: 8px !important;
  cursor: pointer;
}
.sdp-editor {
  flex: 1 1 auto;
  min-height: 480px;
  min-width: 0;
}
/* modo expandido: o card vira overlay fixo em quase toda a tela */
.sdp-card--expanded {
  border-radius: 0;
  bottom: 0;
  left: 0;
  margin: 0;
  overflow: hidden;
  padding: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 30;
}
.sdp-card--expanded .sdp-editor {
  height: 100%;
  min-height: 0;
}
.sdp-card--expanded .sdp-editor ::v-deep .std-root {
  height: 100% !important;
}
.sale-drawing-panel-dark .sdp-card {
  background: #182229;
  border-color: #25d366;
}
.sale-drawing-panel-dark .sdp-title {
  color: #e9edef;
}
.sale-drawing-panel-dark .sdp-sub,
.sale-drawing-panel-dark .sdp-products-label {
  color: #aebac1;
}
.sale-drawing-panel-dark .sdp-sub strong {
  color: #25d366;
}
</style>
